import { CheckOutlined } from "@ant-design/icons";
import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import { useIsLaptopSmUp } from "lib/providers/Responsive";
import { StotlesLogo } from "./StotlesLogo";

import civica from "../../../assets/images/auth_logos/Logo-civica.svg";
import cloudflare from "../../../assets/images/auth_logos/Logo-cloudflare.svg";
import elastic from "../../../assets/images/auth_logos/Logo-elastic.svg";
import ironMountain from "../../../assets/images/auth_logos/Logo-iron-mountain.svg";
import paloAltoNetworks from "../../../assets/images/auth_logos/Logo-paloalto-networks.svg";
import salesforce from "../../../assets/images/auth_logos/Logo-salesforce.svg";
import sap from "../../../assets/images/auth_logos/Logo-SAP.svg";
import splunk from "../../../assets/images/auth_logos/Logo-splunk.svg";
import vodafone from "../../../assets/images/auth_logos/Logo-vodafone.svg";
import zoom from "../../../assets/images/auth_logos/Logo-zoom.svg";

const BENEFITS = [
  "Single, end-to-end system to manage your public sector pipeline",
  "Unify and enrich your bidding data with the best public sector data",
  "Automate your manual tasks with AI you can trust.",
];

/**
 * Customer logos for the panel, exported from the Auth 2026 Figma file. The vendors' standard dark
 * marks — the panel behind them is a pale tint of the brand blue, not a saturated one.
 *
 * Order matches the design's grid, reading across. Salesforce is the one exception: no fresh export
 * landed for it, so it's the old asset with its fill swapped from the previous panel's #E9F0FF to this
 * one's #263247 — safe here because it's a single flat path with one fill and no layering, unlike the
 * Stotles lockup, where the same move broke a knockout between two overlapping shapes.
 */
type TrustLogo = { name: string; src: string };
const TRUST_LOGOS: TrustLogo[] = [
  { name: "Elastic", src: elastic },
  { name: "Civica", src: civica },
  { name: "Cloudflare", src: cloudflare },
  { name: "Vodafone", src: vodafone },
  { name: "Zoom", src: zoom },
  { name: "Iron Mountain", src: ironMountain },
  { name: "Palo Alto Networks", src: paloAltoNetworks },
  { name: "SAP", src: sap },
  { name: "Splunk", src: splunk },
  { name: "Salesforce", src: salesforce },
];

/**
 * Two-column shell for the pre-login pages that want the pitch beside the form — signup today.
 *
 * Above `laptopSmUp` (1024px) the panel is a fixed 640px column and the form takes the rest. Below it the
 * two stack, the panel keeping its styling and running full width under the form. `min-height` rather
 * than `height` so a tall form (several validation errors) scrolls instead of being cropped.
 *
 * The layout owns the Stotles lockup rather than the page, because which of the two positions it takes is
 * a function of the breakpoint: in the panel on desktop, above the form once they stack. Exactly one
 * renders at any width.
 */
export function AuthSplitLayout({ children }: { children: React.ReactNode }) {
  // Rendered on a tier check rather than hidden with CSS, so only one lockup is ever in the DOM — two
  // would mean two "Stotles" images in the accessibility tree, one of them a dead link.
  const isSideBySide = useIsLaptopSmUp();

  return (
    <Split>
      <FormHalf>
        <FormColumn>
          {!isSideBySide && <StotlesLogo />}
          {children}
        </FormColumn>
      </FormHalf>
      <Panel>
        <PanelInner>
          {isSideBySide && <StotlesLogo />}
          <Pitch>
            <Headline>Public sector pipeline, built before the tender goes live.</Headline>
            {BENEFITS.map((benefit) => (
              <Benefit key={benefit}>
                <CheckChip>
                  <CheckOutlined />
                </CheckChip>
                <Text lineHeight={1.5}>{benefit}</Text>
              </Benefit>
            ))}
          </Pitch>
          <TrustedBy>
            <Text fontWeight="500">Trusted by</Text>
            <LogoGrid>
              {TRUST_LOGOS.map(({ name, src }) => (
                <LogoCell key={name}>
                  <Logo src={src} alt="" />
                </LogoCell>
              ))}
            </LogoGrid>
          </TrustedBy>
        </PanelInner>
      </Panel>
    </Split>
  );
}

const Split = styled(Flex)(({ theme }) => ({
  flexDirection: "column",
  alignItems: "stretch",
  minHeight: "100dvh",
  [theme.breakpoints.laptopSmUp]: {
    flexDirection: "row",
  },
}));

const FormHalf = styled(Flex)(({ theme }) => ({
  boxSizing: "border-box",
  alignItems: "center",
  justifyContent: "center",
  padding: "80px 16px",
  backgroundColor: theme.colors.sysBackgroundDefault,
  [theme.breakpoints.laptopSmUp]: {
    flex: "1 1 0",
    minWidth: 0,
    padding: "24px 8px",
  },
}));

const FormColumn = styled(Flex)({
  flexDirection: "column",
  gap: 32,
  width: "100%",
  maxWidth: 480,
});

// Fixed 640px beside the form, full width once stacked. Figma counts the 80px padding inside that width,
// hence `border-box`.
const Panel = styled(Flex)(({ theme }) => ({
  boxSizing: "border-box",
  flexDirection: "column",
  justifyContent: "center",
  padding: "80px 16px",
  // A 12% wash of the brand blue rather than a flat token, so it composites over whatever it sits on.
  backgroundColor: `color-mix(in srgb, ${theme.colors.sysPrimaryDefault} 12%, transparent)`,
  [theme.breakpoints.laptopSmUp]: {
    flex: "0 0 auto",
    width: 640,
    padding: 80,
  },
}));

const PanelInner = styled(Flex)({
  flexDirection: "column",
  justifyContent: "center",
  gap: 48,
  width: "100%",
  maxWidth: 480,
  alignSelf: "center",
});

const Pitch = styled(Flex)({
  flexDirection: "column",
  gap: 18,
});

// 20px at weight 500. `Text`'s own heading flags can't express that pairing — `h2` is 20px but forces 600,
// and it applies that after the `fontWeight` prop — so the values are set here instead. `&&` so the
// override lands whichever order Emotion inserts the two classes in.
const Headline = styled(Text)({
  "&&": {
    fontSize: 20,
    fontWeight: 500,
    lineHeight: 1.5,
    textWrap: "balance",
  },
});

const Benefit = styled(Flex)({
  alignItems: "center",
  gap: 11,
});

const CheckChip = styled(Flex)(({ theme }) => ({
  flex: "0 0 auto",
  width: 16,
  height: 16,
  alignItems: "center",
  justifyContent: "center",
  borderRadius: 3,
  backgroundColor: theme.colors.sysPrimaryDefault,
  color: theme.colors.sysTextWhite,
  fontSize: 12,
}));

const TrustedBy = styled(Flex)({
  flexDirection: "column",
  gap: 16,
  letterSpacing: -0.28,
});

const LogoGrid = styled(Flex)({
  flexWrap: "wrap",
  gap: "24px 40px",
});

const LogoCell = styled(Flex)({
  width: 80,
  height: 40,
  alignItems: "center",
  justifyContent: "center",
});

const Logo = styled.img({
  display: "block",
  width: "100%",
  height: "auto",
  maxHeight: 40,
  objectFit: "contain",
});
